import { useSuspenseQuery } from '@tanstack/react-query';
import bbox from '@turf/bbox';
import { LngLatBoundsLike } from 'mapbox-gl';

import { CellsGeojson, GetConservationFilesSuccessDto, PolygonsGeojson } from '@/api/rest/resources/conservation';
import { useMembershipType } from '@/hooks/useMembershipType';
import { useProjectId } from '@/pages/shared/hooks/useProjectId';

import { useConservationFile } from './useConservation';
import { useConservationProjectName } from './useConservationProjectName';

const CONSERVATION_NOT_FOUND_ERROR = 'Conservation project name not found';

export const useProjectBoundary = (projectIdArg?: string) => {
  const projectName = useConservationProjectName(projectIdArg);

  if (!projectName) {
    throw new Error(CONSERVATION_NOT_FOUND_ERROR);
  }

  return useConservationFile<PolygonsGeojson>({
    projectName,
    fileName: 'polygon.json',
  });
};

export const useProjectBounds = (projectIdArg?: string) => {
  const membershipType = useMembershipType();

  const projectId = useProjectId();
  const projectName = useConservationProjectName(projectIdArg ?? projectId);

  if (!projectName) {
    throw new Error(CONSERVATION_NOT_FOUND_ERROR);
  }

  const polygonModule = useProjectBoundary();

  return useSuspenseQuery({
    queryKey: [membershipType, 'project', 'conservation', projectName, 'boundary', 'bounds', polygonModule],
    queryFn: () => bbox(polygonModule) as LngLatBoundsLike,
  });
};

export const useProjectCells = (projectId?: string, year?: string) => {
  const projectName = useConservationProjectName(projectId);

  if (!projectName) {
    throw new Error(CONSERVATION_NOT_FOUND_ERROR);
  }

  const suffix = year ? `_${year}` : '';

  const cells =
    useConservationFile<CellsGeojson>({
      projectName,
      fileName: `cells${suffix}.json` as keyof GetConservationFilesSuccessDto,
    }) ?? ({} as CellsGeojson);

  return {
    ...cells,
    features: cells?.features?.map((feature, index) => ({
      ...feature,
      id: index + 1,
    })),
  } as CellsGeojson;
};
